import { Button as ButtonBase, ProgressBar } from "react-aria-components";
import { styled } from "../../../../styled-system/jsx";
import type { ComponentProps, ReactNode } from "react";
import { ImSpinner2 } from "react-icons/im";
import { css, cva } from "../../../../styled-system/css";

const commonButtonStyles = css.raw({
  justifyContent: "center",
  border: "1px solid",
  borderRadius: "button",
  minHeight: "button",
  paddingY: "small",
  paddingX: "medium",
  fontSize: "xsmall",
  fontWeight: "700",
  letterSpacing: "2.5px",
  lineHeight: "normal",
  textTransform: "uppercase",
  _disabled: {
    cursor: "not-allowed",
    opacity: 0.5,
  },
});

export const buttonRecipe = cva({
  base: {
    all: "unset",
    boxSizing: "border-box",
    cursor: "pointer",
    display: "flex",
    gap: "xsmall",
    alignItems: "center",
    textWrap: "nowrap",
    transition: "all 0.3s ease 0s",
    flexWrap: "nowrap",
    _focusVisible: {
      outline: "2px solid {colors.bootstrapBlue}",
    },
    "&[data-pending]": {
      opacity: 0.75,
    },
  },
  variants: {
    variant: {
      link: {
        color: "trellisPalette.13",
        textDecoration: "underline",
        fontWeight: "600",
        _disabled: {
          cursor: "default",
          color: "#aaa",
        },
      },
      primary: {
        ...commonButtonStyles,
        borderColor: "button.primary.borderColor",
        color: "button.primary.color",
        backgroundColor: "button.primary.backgroundColor",
      },
      secondary: {
        ...commonButtonStyles,
        borderColor: "button.secondary.borderColor",
        color: "button.secondary.color",
        backgroundColor: "button.secondary.backgroundColor",
      },
      contentOnly: {
        border: "none",
        backgroundColor: "transparent",
        _disabled: {
          cursor: "default",
          color: "#aaa",
          _hover: {
            backgroundColor: "transparent",
          },
        },
        _hover: {
          backgroundColor: "lightgrey",
        },
      },
    },
    size: {
      small: {
        borderRadius: "8px",
        minHeight: "unset",
        paddingY: "xsmall",
        paddingX: "small",
        fontWeight: "600",
        letterSpacing: "1.5px",
      },
    },
  },
  defaultVariants: {
    variant: "primary",
  },
});

const StyledButton = styled(ButtonBase, buttonRecipe);

export function Button({ children, ...props }: ComponentProps<typeof StyledButton> & { children: ReactNode }) {
  return (
    <StyledButton {...props}>
      {({ isPending }) => {
        return (
          <>
            {children}
            {isPending && (
              <ProgressBar aria-label="Saving..." isIndeterminate>
                <ImSpinner2
                  className={css({
                    animation: "spin",
                  })}
                />
              </ProgressBar>
            )}
          </>
        );
      }}
    </StyledButton>
  );
}

export const LinkButton = (props: ComponentProps<typeof Button>) => <Button variant="link" {...props} />;
